import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, ActivityIndicator, Button, Surface } from 'react-native-paper';
import PatientAuthService from '@/services/PatientAuthService';
import PatientEmailService from '@/services/patientEmailService';
import BaseContainer from '@/components/BaseContainer';
import { Color } from '@/GlobalStyles';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import ScreenMargin from '@/components/ScreenMargin';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useGetPatientFormDataQuery, useGetExpiredTokenInfoLazyQuery } from '@/data/data';
import { useTranslation } from 'react-i18next';

interface TokenError {
  type: 'invalid' | 'expired' | 'used' | 'network';
  message: string;
}

interface PatientData {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  sexe?: string;
  size?: number;
  birthday?: string;
  photicEffect?: boolean;
  glasses?: boolean;
}

interface ExpiredTokenData {
  patientId: string;
  patientName: string;
  patientEmail?: string;
  surgeonCognitoId: string;
  surgeonName: string;
  clinicName?: string;
}

export default function TokenValidationScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const router = useRouter();
  const { t } = useTranslation();
  const [error, setError] = useState<TokenError | null>(null);
  const [validating, setValidating] = useState(true);
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [expiredTokenData, setExpiredTokenData] = useState<ExpiredTokenData | null>(null);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Utiliser la vraie query GraphQL pour récupérer les données patient
  // On ne lance la query qu'après avoir nettoyé l'ancien token
  const {
    data: patientFormData,
    loading: queryLoading,
    error: queryError,
  } = useGetPatientFormDataQuery({
    variables: { token: token || '' },
    skip: !token || !shouldFetchData,
  });

  // Query lazy pour récupérer les infos d'un token expiré
  const [getExpiredTokenInfo] = useGetExpiredTokenInfoLazyQuery();

  // Premier useEffect: sauvegarder le nouveau token AVANT la validation
  useEffect(() => {
    const saveTokenForValidation = async () => {
      // Ne rien faire si le token n'est pas encore chargé depuis les params
      // (évite d'afficher une erreur prématurée au premier rendu)
      if (!token) {
        return;
      }

      try {
        // Garder le loader visible pendant tout le processus
        setValidating(true);

        // Récupérer l'ancien token stocké SANS validation pour éviter de nettoyer le token temporaire
        const oldTokenData = await PatientAuthService.getTokenRaw();

        // Si un ancien token existe et qu'il est différent du nouveau, le nettoyer
        if (oldTokenData && oldTokenData.token !== token) {
          console.log("[TokenValidation] Nouveau token détecté, nettoyage de l'ancien token");
          await PatientAuthService.clearToken();
        }

        // Sauvegarder le nouveau token AVANT de faire la query
        // On utilise des données temporaires qui seront complétées après la validation
        console.log('[TokenValidation] Sauvegarde du token pour permettre la query GraphQL');
        await PatientAuthService.saveToken({
          token,
          tokenId: 'temp-validating', // Temporaire, sera mis à jour après validation
          patientId: 'temp-validating',
          expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(), // 48h par défaut
          patientName: 'Validation en cours...',
        });

        // Autoriser maintenant la requête GraphQL
        console.log('[TokenValidation] Autorisation de la requête GraphQL');
        setShouldFetchData(true);
        // Ne pas changer validating ici, laisser le second useEffect le gérer
      } catch (err) {
        console.error('[TokenValidation] Erreur lors de la sauvegarde du token:', err);
        setError({
          type: 'network',
          message: t('patient.technicalError'),
        });
        setValidating(false);
      }
    };

    saveTokenForValidation();
  }, [token, t]);

  useEffect(() => {
    // Si shouldFetchData n'est pas encore autorisé, ne rien faire
    if (!shouldFetchData) return;

    if (!token) return;

    const validateAndSetup = async () => {
      try {
        // Attendre que la query soit terminée avant de traiter
        if (queryLoading) {
          // Rester en mode "validation" tant que la query charge
          setValidating(true);
          return;
        }

        // Si on arrive ici, la query est terminée (succès ou erreur)
        setValidating(true);
        setError(null);

        // Gérer les erreurs de la query
        if (queryError) {
          console.error('Erreur lors de la récupération des données:', queryError);
          // Nettoyer le token invalide
          await PatientAuthService.clearToken();
          setError({
            type: 'network',
            message: t('patient.connectionError'),
          });
          setValidating(false);
          return;
        }

        // Vérifier si des données ont été trouvées
        const tokenData = patientFormData?.patientTempTokens?.[0];

        if (!tokenData) {
          // Token non trouvé par la query normale (peut être expiré ou invalide)
          // Essayer de récupérer les infos via la query sans filtre de date
          try {
            const { data: expiredData } = await getExpiredTokenInfo({
              variables: { token: token || '' },
            });

            const expiredTokenInfo = expiredData?.patientTempTokens?.[0];

            if (expiredTokenInfo) {
              // Token trouvé - vérifier s'il est expiré ou utilisé
              const expiresAt = new Date(expiredTokenInfo.expiresAt);
              const patient = expiredTokenInfo.patient_tokens;
              const surgeon = patient?.chirurgien;

              if (expiredTokenInfo.isUsed) {
                // Token déjà utilisé
                await PatientAuthService.clearToken();
                setError({
                  type: 'used',
                  message: t('patient.linkUsed'),
                });
              } else if (expiresAt <= new Date()) {
                // Token expiré - stocker les infos pour la demande de régénération
                await PatientAuthService.clearToken();

                if (surgeon?.cognitoToken) {
                  setExpiredTokenData({
                    patientId: expiredTokenInfo.patientId,
                    patientName: `${patient?.prenom || ''} ${patient?.nom || ''}`.trim(),
                    patientEmail: patient?.email || undefined,
                    surgeonCognitoId: surgeon.cognitoToken,
                    surgeonName: `${surgeon?.prenom || ''} ${surgeon?.nom || ''}`.trim(),
                    clinicName: surgeon?.clinique || undefined,
                  });
                }

                setError({
                  type: 'expired',
                  message: t('patient.linkExpired'),
                });
              } else {
                // Token valide mais pas trouvé ? Ne devrait pas arriver
                await PatientAuthService.clearToken();
                setError({
                  type: 'invalid',
                  message: t('patient.linkInvalid'),
                });
              }
            } else {
              // Token vraiment invalide
              await PatientAuthService.clearToken();
              setError({
                type: 'invalid',
                message: t('patient.linkInvalid'),
              });
            }
          } catch {
            // Erreur lors de la vérification, considérer comme invalide
            await PatientAuthService.clearToken();
            setError({
              type: 'invalid',
              message: t('patient.linkInvalid'),
            });
          }

          setValidating(false);
          return;
        }

        // Vérifier si le token a déjà été utilisé
        if (tokenData.isUsed) {
          // Token déjà utilisé, nettoyer
          await PatientAuthService.clearToken();
          setError({
            type: 'used',
            message: t('patient.linkUsed'),
          });
          setValidating(false);
          return;
        }

        // Vérifier si le token a expiré (double vérification côté client)
        const expiresAt = new Date(tokenData.expiresAt);
        if (expiresAt <= new Date()) {
          // Token expiré - récupérer les infos du chirurgien
          const patient = tokenData.patient_tokens;
          // Note: Cette branche ne devrait pas être atteinte car la query filtre par date
          await PatientAuthService.clearToken();
          setError({
            type: 'expired',
            message: t('patient.linkExpired'),
          });
          setValidating(false);
          return;
        }

        // Récupérer les données patient depuis la query
        const patient = tokenData.patient_tokens || {};
        const extractedPatientData = {
          id: tokenData.patientId,
          nom: patient.nom || '',
          prenom: patient.prenom || '',
          email: patient.email || '',
          telephone: patient.telephone || '',
          sexe: patient.sexe,
          size: patient.size,
          birthday: patient.birthday,
          photicEffect: patient.photicEffect,
          glasses: patient.glasses,
        };

        setPatientData(extractedPatientData);

        // Mettre à jour le token avec les vraies données patient (remplacer les données temporaires)
        console.log('[TokenValidation] Token validé avec succès, mise à jour des données');
        await PatientAuthService.saveToken({
          token,
          tokenId: tokenData.id,
          patientId: tokenData.patientId,
          expiresAt: tokenData.expiresAt,
          patientName: `${extractedPatientData.prenom} ${extractedPatientData.nom}`,
          email: extractedPatientData.email,
          telephone: extractedPatientData.telephone,
        });

        // Initialiser les données du formulaire avec les données patient si pas déjà présentes
        console.log(
          '[TokenValidation] FormData depuis GraphQL:',
          JSON.stringify(tokenData.formData, null, 2)
        );

        if (
          tokenData.formData &&
          typeof tokenData.formData === 'object' &&
          Object.keys(tokenData.formData).length > 0
        ) {
          console.log('[TokenValidation] Chargement des formData existantes depuis le serveur');
          await PatientAuthService.saveFormData(tokenData.formData);
        } else {
          console.log('[TokenValidation] Initialisation de nouvelles formData');
          // Initialiser le formulaire avec les données de base du patient
          const initialFormData = {
            form: {
              nom: extractedPatientData.nom,
              prenom: extractedPatientData.prenom,
              sexe:
                extractedPatientData.sexe === 1
                  ? 'M'
                  : extractedPatientData.sexe === 2
                    ? 'F'
                    : undefined,
              birthday: extractedPatientData.birthday,
              taille: extractedPatientData.size,
              photicEffect: extractedPatientData.photicEffect,
              glasses: extractedPatientData.glasses,
            },
            metadata: {
              currentStep: 'form',
              completedSteps: [],
              lastModifiedAt: new Date().toISOString(),
            },
          };
          await PatientAuthService.saveFormData(initialFormData);
        }

        // Seulement maintenant, on peut marquer la validation comme terminée
        setValidating(false);
      } catch (err) {
        console.error('Erreur lors de la validation du token:', err);
        setError({
          type: 'network',
          message: t('patient.connectionError'),
        });
        setValidating(false);
      }
    };

    validateAndSetup();
  }, [token, patientFormData, queryLoading, queryError, t]);

  const handleRetry = () => {
    setValidating(true);
    setError(null);
    // La query se relancera automatiquement avec le refetch
  };

  const handleRequestNewLink = async () => {
    if (!expiredTokenData) return;

    setSendingRequest(true);
    setRequestError(null);

    try {
      await PatientEmailService.sendLinkRegenerationRequestEmail({
        patientName: expiredTokenData.patientName,
        patientEmail: expiredTokenData.patientEmail,
        surgeonCognitoId: expiredTokenData.surgeonCognitoId,
        surgeonName: expiredTokenData.surgeonName,
        clinicName: expiredTokenData.clinicName,
        patientId: expiredTokenData.patientId,
      });

      setRequestSent(true);
    } catch (err) {
      console.error("Erreur lors de l'envoi de la demande:", err);
      setRequestError(t('patient.requestNewLinkError'));
    } finally {
      setSendingRequest(false);
    }
  };

  if (validating) {
    return (
      <BaseContainer>
        <View style={styles.centerContainer}>
          <Surface style={styles.surface} elevation={2}>
            <ActivityIndicator size="large" color={Color.MainBleu} />
            <Text style={styles.loadingTitle}>{t('patient.validating')}</Text>
            <Text style={styles.loadingSubtitle}>{t('patient.verifyingLink')}</Text>
          </Surface>
        </View>
      </BaseContainer>
    );
  }

  if (error) {
    const getErrorIcon = () => {
      switch (error.type) {
        case 'expired':
          return 'clock-alert-outline';
        case 'used':
          return 'check-circle-outline';
        case 'network':
          return 'wifi-off';
        default:
          return 'alert-circle-outline';
      }
    };

    const getErrorColor = () => {
      switch (error.type) {
        case 'expired':
          return '#FF9800';
        case 'used':
          return '#4CAF50';
        case 'network':
          return '#2196F3';
        default:
          return '#F44336';
      }
    };

    console.log(error.type === 'expired' && expiredTokenData && !requestSent);
    console.log(error.type, expiredTokenData, requestSent);

    return (
      <BaseContainer>
        <KeyboardAwareScrollView>
          <ScreenMargin>
            <Surface style={styles.errorSurface} elevation={1}>
              <View style={styles.errorHeader}>
                <View style={[styles.iconContainer, { backgroundColor: getErrorColor() + '20' }]}>
                  <Icon name={getErrorIcon()} size={48} color={getErrorColor()} />
                </View>
                <Text style={[styles.errorTitle, { color: Color.MainNoir }]}>
                  {error.type === 'expired' && 'Lien expiré'}
                  {error.type === 'used' && 'Lien déjà utilisé'}
                  {error.type === 'network' && 'Erreur de connexion'}
                  {error.type === 'invalid' && 'Lien invalide'}
                </Text>
                <Text style={styles.errorMessage}>{error.message}</Text>
              </View>

              <View style={styles.solutionSection}>
                <Text style={styles.sectionTitle}>Solutions possibles</Text>
                <View style={styles.solutionList}>
                  <View style={styles.solutionItem}>
                    <Icon name="wifi" size={24} color={Color.MainBleu} />
                    <Text style={styles.solutionText}>Vérifiez votre connexion internet</Text>
                  </View>
                  <View style={styles.solutionItem}>
                    <Icon name="phone" size={24} color={Color.MainBleu} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.solutionText}>Contactez votre chirurgien</Text>
                      <Text style={styles.solutionDescription}>Pour obtenir un nouveau lien</Text>
                    </View>
                  </View>
                  <View style={styles.solutionItem}>
                    <Icon name="refresh" size={24} color={Color.MainBleu} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.solutionText}>Réessayez plus tard</Text>
                      <Text style={styles.solutionDescription}>
                        Le problème peut être temporaire
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.buttonContainer}>
                {error.type === 'network' ? (
                  <Button
                    mode="outlined"
                    onPress={handleRetry}
                    icon="refresh"
                    style={styles.button}
                    textColor={Color.MainBleu}
                  >
                    {t('patient.retry')}
                  </Button>
                ) : null}
                {error.type === 'expired' && expiredTokenData && !requestSent ? (
                  <Button
                    mode="contained"
                    onPress={handleRequestNewLink}
                    loading={sendingRequest}
                    disabled={sendingRequest}
                    icon="email-send"
                    buttonColor={Color.MainBleu}
                    style={styles.requestButton}
                  >
                    {t('patient.requestNewLink')}
                  </Button>
                ) : null}
                {error.type === 'expired' && requestSent ? (
                  <View style={styles.successMessage}>
                    <Icon name="check-circle" size={24} color={Color.VertElectrique} />
                    <Text style={styles.successText}>{t('patient.requestNewLinkSent')}</Text>
                  </View>
                ) : null}
                {requestError ? <Text style={styles.requestErrorText}>{requestError}</Text> : null}
              </View>
            </Surface>
          </ScreenMargin>
        </KeyboardAwareScrollView>
      </BaseContainer>
    );
  }

  // Écran de succès avec informations patient
  if (patientData) {
    return (
      <BaseContainer>
        <KeyboardAwareScrollView>
          <ScreenMargin>
            <Surface style={styles.successSurface} elevation={1}>
              <View style={styles.successHeader}>
                <View
                  style={[styles.iconContainer, { backgroundColor: Color.VertElectrique + '20' }]}
                >
                  <Icon name="check-circle" size={48} color={Color.VertElectrique} />
                </View>
                <Text style={[styles.successTitle, { color: Color.MainNoir }]}>
                  {t('patient.accessGranted')}
                </Text>
                <Text style={styles.welcomeMessage}>
                  {t('patient.greeting')} {patientData.prenom} {patientData.nom}
                </Text>
              </View>

              <Surface style={styles.infoCard} elevation={0}>
                <View style={styles.infoRow}>
                  <Icon name="email" size={20} color={Color.MainBleu} />
                  <View style={styles.infoContent}>
                    <Text style={styles.infoLabel}>{t('patient.email')}</Text>
                    <Text style={styles.infoValue}>{patientData.email}</Text>
                  </View>
                </View>
              </Surface>

              <View style={styles.stepsSection}>
                <Text style={styles.sectionTitle}>{t('patient.nextSteps')}</Text>
                <View style={styles.stepsList}>
                  <View style={styles.stepItem}>
                    <View style={styles.stepNumber}>
                      <Text style={styles.stepNumberText}>1</Text>
                    </View>
                    <View style={styles.stepContent}>
                      <Text style={styles.stepTitle}>{t('patient.completeForm')}</Text>
                      <Text style={styles.stepDescription}>{t('patient.completeFormDesc')}</Text>
                    </View>
                  </View>
                  <View style={styles.stepItem}>
                    <View style={styles.stepNumber}>
                      <Text style={styles.stepNumberText}>2</Text>
                    </View>
                    <View style={styles.stepContent}>
                      <Text style={styles.stepTitle}>{t('patient.stepValidation')}</Text>
                      <Text style={styles.stepDescription}>{t('patient.stepValidationDesc')}</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.buttonContainer}>
                <Button
                  mode="contained"
                  onPress={() => {
                    // Rediriger vers la première page du formulaire
                    // Le token est maintenant stocké et sera utilisé par les autres routes
                    router.replace('/patient/form/form');
                  }}
                  buttonColor={Color.MainBleu}
                  icon="arrow-right"
                  contentStyle={{ flexDirection: 'row-reverse' }}
                  style={styles.primaryButton}
                >
                  {t('patient.startForm')}
                </Button>
              </View>
            </Surface>
          </ScreenMargin>
        </KeyboardAwareScrollView>
      </BaseContainer>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  surface: {
    padding: 32,
    borderRadius: 12,
    backgroundColor: 'white',
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
  },
  loadingTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 20,
    marginBottom: 8,
    color: Color.MainNoir,
  },
  loadingSubtitle: {
    fontSize: 14,
    color: Color.MainGris,
    textAlign: 'center',
  },
  errorSurface: {
    padding: 24,
    borderRadius: 12,
    backgroundColor: 'white',
    marginTop: 20,
    marginBottom: 20,
  },
  errorHeader: {
    alignItems: 'center',
    marginBottom: 32,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    color: Color.MainGris,
    paddingHorizontal: 16,
  },
  solutionSection: {
    marginTop: 32,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 20,
    color: Color.MainNoir,
  },
  solutionList: {
    gap: 16,
  },
  solutionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  solutionText: {
    fontSize: 16,
    color: Color.MainNoir,
    fontWeight: '500',
  },
  solutionDescription: {
    fontSize: 14,
    color: Color.MainGris,
    marginTop: 2,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'center',
    marginTop: 16,
  },
  button: {
    flex: 1,
  },
  successSurface: {
    padding: 24,
    borderRadius: 12,
    backgroundColor: 'white',
    marginTop: 20,
    marginBottom: 20,
  },
  successHeader: {
    alignItems: 'center',
    marginBottom: 32,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  welcomeMessage: {
    fontSize: 18,
    textAlign: 'center',
    color: Color.MainBleu,
    fontWeight: '500',
  },
  infoCard: {
    padding: 20,
    borderRadius: 8,
    backgroundColor: Color.BlancCasse,
    marginBottom: 32,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  infoContent: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 12,
    color: Color.MainGris,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  infoValue: {
    fontSize: 16,
    color: Color.MainNoir,
    fontWeight: '500',
  },
  stepsSection: {
    marginBottom: 32,
  },
  stepsList: {
    gap: 20,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  stepNumber: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Color.MainBleu,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumberText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Color.MainNoir,
    marginBottom: 4,
  },
  stepDescription: {
    fontSize: 14,
    color: Color.MainGris,
  },
  primaryButton: {
    flex: 1,
    paddingVertical: 4,
  },
  requestButton: {
    flex: 1,
    paddingVertical: 4,
  },
  successMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: Color.VertElectrique + '15',
    borderRadius: 8,
    flex: 1,
  },
  successText: {
    color: Color.VertElectrique,
    fontSize: 16,
    fontWeight: '500',
  },
  requestErrorText: {
    color: '#F44336',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
});
